
import type { PropOptions } from 'vue';
import Vue from 'vue';
import PlusSvg from './images/plus.vue.svg';
import type { AutocompleteCompany } from './companiesAutocompleteMixin';
import CompaniesAutocompleteMixin from './companiesAutocompleteMixin';
import AutocompleteDropdownV2 from '~/components/common/AutocompleteDropdownV2.vue';
import { src, srcSet } from '~/components/utils/responsive';

const LOGO_SIZE = 36;

export default Vue.extend({
  name: 'CompaniesAutocompleteV2',
  components: {
    AutocompleteDropdownV2,
    PlusSvg,
  },
  mixins: [CompaniesAutocompleteMixin],
  model: {
    prop: 'value',
    event: 'change:query',
  },
  props: {
    value: {
      type: String,
      default: '',
    } as PropOptions<string>,
    excluded: {
      type: Array,
      default: () => [],
    } as PropOptions<AutocompleteCompany[]>,
    isHideAddCompany: {
      type: Boolean,
      default: false,
    } as PropOptions<boolean>,
    label: {
      type: String,
      default: 'company',
    } as PropOptions<string>,
    name: {
      type: String,
      default: 'company',
    } as PropOptions<string>,
    placeholder: {
      type: String,
      default: 'Search for a company',
    } as PropOptions<string>,
    isValid: {
      type: Boolean,
      default: true,
    } as PropOptions<boolean>,
    errorMessage: {
      type: String,
      default: '',
    } as PropOptions<string>,
    disabled: {
      type: Boolean,
      default: false,
    } as PropOptions<boolean>,
    appendText: {
      type: String,
      default: '',
    } as PropOptions<string>,
    variant: {
      type: String,
      default: 'default',
      validator: (value: string) => ['default', 'borderless'].includes(value),
    },
    addOnBlur: {
      type: Boolean,
      default: false,
    },
  },
  data(): { query: string; suspendBlur: boolean } {
    return {
      query: this.value,
      suspendBlur: true,
    };
  },
  computed: {
    itemClass(): Record<string, boolean> {
      return {
        'companies-autocomplete__item-title': true,
        'companies-autocomplete__item-title--highlight': !!this.query,
      };
    },
    filteredCompanies(): Array<AutocompleteCompany> {
      // @ts-ignore
      return this.companies.filter(company => !this.isExcluded(company));
    },
  },
  watch: {
    value(newVal) {
      if (this.query !== newVal) {
        this.onQuery({ text: newVal, value: newVal });
      }
    },
  },
  methods: {
    onBlur() {
      this.suspendBlur = !this.addOnBlur;

      // blur fires before @push
      // when user selects company from dropdown we want to prevent blur event
      // so if user selects company from dropdown we set suspendBlur to true and code below will not be invoked
      setTimeout(async () => {
        if (!this.suspendBlur && this.query) {
          // @ts-ignore
          const company = await this.createCompany({ name: this.query });
          this.$emit('change', company);
        } else if (!this.suspendBlur) {
          this.handleQuery('');
        }
      }, 500);
    },
    logoSrcSet(logo: string) {
      return srcSet(logo, LOGO_SIZE, LOGO_SIZE);
    },
    logoSrc(logo: string) {
      return src(logo, LOGO_SIZE, LOGO_SIZE);
    },
    getNewCompanyItem(term: string) {
      return {
        name: term,
        isNewCompany: true,
      };
    },
    isExcluded(company: AutocompleteCompany) {
      const companyName = company.name.toUpperCase();
      return (this.excluded as AutocompleteCompany[]).some(({ name }) => name.toUpperCase() === companyName);
    },
    onQuery(query: { text: string; value: string }) {
      this.$emit('type', query);
      this.$emit('change:query', query.text);
      this.handleQuery(query.text);
    },
    async onPush(item: AutocompleteCompany & { text: string; value: string; isNewCompany?: boolean }) {
      // prevent blur event
      this.suspendBlur = true;
      let company: AutocompleteCompany;

      if (item.isNewCompany) {
        // @ts-ignore
        company = await this.createCompany({ name: item.name });
      } else {
        company = { ...item, name: item.text };
      }

      this.$emit('change', Object.assign({}, company));
      this.$emit('change:query', this.query);
      this.handleQuery(company.name);
    },
    handleQuery(term: string) {
      this.query = term;
      // @ts-ignore
      this.searchCompanies(term);
    },
  },
});
